import { useRef, useMemo, useEffect } from "react";
import * as THREE from "three";

export function useCircleTexture() {
  return useMemo(() => {
    const size = 128;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    const gradient = ctx.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2,
    );
    /* A solid core with a thin falloff, rather than a gradient across the whole
       sprite. The old soft profile left every dot looking blurred once the
       alpha cutoff bit into it; keeping alpha flat out to 0.72 of the radius
       gives a crisp disc while the short ramp still antialiases the rim. */
    gradient.addColorStop(0, "rgba(255,255,255,1)");
    gradient.addColorStop(0.72, "rgba(255,255,255,1)");
    gradient.addColorStop(0.9, "rgba(255,255,255,0.5)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 4;
    texture.needsUpdate = true;
    return texture;
  }, []);
}

/* The swirl block is injected only for the layer that actually swirls. The
   ambient field and the soft underlay share this shader but never converge, and
   compiling dead uniforms into their programs would only invite someone to
   wonder later why they are there. */
const SWIRL_UNIFORM_DECLS = `
         uniform float uSwirl;
         uniform float uSwirlFade;
         uniform float uSwirlRadius;
         uniform float uSwirlTurns;
         uniform float uSwirlScatter;
         uniform float uSwirlLift;
         uniform float uSwirlStagger;`;

/* `transformed` arrives here already settled - surface position plus this
   frame's emit travel and drift. The swirl displaces it back out into a
   rotating cloud, and `uSwirl` walking 1 -> 0 is what draws the figure out of
   that cloud. Because the displacement is a function of the settled position,
   no second copy of the 450,000 positions is needed on either side of the bus.

   Each particle takes its own angle from `aPhase`, which is already a uniform
   random turn, so the cloud is smeared around the model's vertical axis instead
   of being a rigidly rotated copy of the dragon. One hash off the same
   attribute supplies the radial reach and the arrival stagger. */
const SWIRL_VERTEX = `
         float swirlHash = fract(sin(aPhase * 91.7) * 43758.5453);
         float swirlDelay = swirlHash * uSwirlStagger;
         float swirlT = clamp(
           (uSwirl - swirlDelay) / max(1e-4, 1.0 - swirlDelay), 0.0, 1.0);
         float swirlAngle =
           swirlT * (uSwirlTurns * 6.2831853 + (aPhase - 3.1415927) * uSwirlScatter);
         float swirlCos = cos(swirlAngle);
         float swirlSin = sin(swirlAngle);
         transformed.xz = vec2(
           transformed.x * swirlCos - transformed.z * swirlSin,
           transformed.x * swirlSin + transformed.z * swirlCos);
         // outward along the horizontal radius; the max() keeps a particle
         // sitting on the axis from dividing by zero
         vec2 swirlOut = transformed.xz / max(length(transformed.xz), 1e-4);
         transformed.xz += swirlOut * (uSwirlRadius * swirlT * (0.35 + 0.65 * swirlHash));
         transformed.y += uSwirlLift * swirlT * (swirlHash * 2.0 - 1.0);`;

/* Per-particle drift, done on the GPU.

   Moving each point individually from JS would mean rewriting a
   several-hundred-thousand element position buffer every frame; injecting the
   offset into the vertex shader costs nothing per frame instead. Each particle
   carries a random `aPhase` so they wander out of step with one another rather
   than pulsing in unison, and the three axes use different frequencies so the
   paths do not degenerate into straight lines. */
export function makeDriftOnBeforeCompile(
  shaderRef,
  amplitude,
  speed,
  emit = {},
  swirl = null,
) {
  const fade = new THREE.Color();
  fade.setHex(emit.fadeColor ?? 0xfff7f0, THREE.SRGBColorSpace);

  return (shader) => {
    shader.uniforms.uTime = { value: 0 };
    shader.uniforms.uDriftAmp = { value: amplitude };
    shader.uniforms.uDriftSpeed = { value: speed };
    shader.uniforms.uEmitDistance = { value: emit.distance ?? 0 };
    shader.uniforms.uEmitSpeed = { value: emit.speed ?? 0 };
    shader.uniforms.uEmitSize = { value: emit.size ?? 1 };
    shader.uniforms.uFadeColor = { value: fade };

    if (swirl) {
      // starts fully scattered and invisible, and stays there until the site is
      // revealed: DragonParticles advances both every frame after that
      shader.uniforms.uSwirl = { value: 1 };
      shader.uniforms.uSwirlFade = { value: 0 };
      shader.uniforms.uSwirlRadius = { value: swirl.radius ?? 0 };
      shader.uniforms.uSwirlTurns = { value: swirl.turns ?? 0 };
      shader.uniforms.uSwirlScatter = { value: swirl.scatter ?? 0 };
      shader.uniforms.uSwirlLift = { value: swirl.lift ?? 0 };
      shader.uniforms.uSwirlStagger = { value: swirl.stagger ?? 0 };
    }

    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `#include <common>
         attribute float aPhase;
         attribute float aDrift;
         attribute float aEmit;
         attribute vec3 aNormal;
         uniform float uTime;
         uniform float uDriftAmp;
         uniform float uDriftSpeed;
         uniform float uEmitDistance;
         uniform float uEmitSpeed;
         uniform float uEmitSize;
         uniform vec3 uFadeColor;${swirl ? SWIRL_UNIFORM_DECLS : ""}`,
      )
      /* Emitters ride a 0..1 sawtooth outward along their surface normal and
         restart at the surface, so the model continuously sheds particles. The
         phase offset is per-particle, which keeps the stream continuous instead
         of pulsing in waves. Fading is done by mixing toward the page colour
         rather than by alpha, because alphaTest would clip a fading sprite
         abruptly instead of easing it out. */
      .replace(
        "#include <color_vertex>",
        `#include <color_vertex>
         float travel = fract(uTime * uEmitSpeed + aPhase * 0.15915);
         float emitFade = mix(
           1.0,
           smoothstep(0.0, 0.12, travel) * (1.0 - smoothstep(0.45, 1.0, travel)),
           // aEmit doubles as the per-particle reach, so any non-zero value marks
           // an emitter rather than testing against a half-way threshold
           step(0.001, aEmit)
         );
         vColor = mix(uFadeColor, vColor, emitFade);${
           swirl
             ? `
         /* The swirl fades up the same way, for the same reason: alphaTest
            would hold a fading sprite at full strength and then drop it. */
         vColor = mix(uFadeColor, vColor, uSwirlFade);`
             : ""
         }`,
      )
      .replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
         transformed += aNormal * (travel * uEmitDistance * aEmit);
         float driftT = uTime * uDriftSpeed + aPhase;
         float amp = uDriftAmp * aDrift;
         transformed.x += sin(driftT) * amp;
         transformed.y += cos(driftT * 0.83 + 1.7) * amp;
         transformed.z += sin(driftT * 0.61 + 3.1) * amp;${
           swirl ? SWIRL_VERTEX : ""
         }`,
      )
      /* Emitted particles swell as they travel, so the stream leaving the model
         reads as distinct motes rather than more of the same fine grain. Applied
         before the size-attenuation block below, so perspective still scales it.

         Shrinking to nothing is the other half of the swirl's fade-up. It is
         not on its own what keeps the hidden dragon harmless, though: a driver
         is free to clamp gl_PointSize up to ALIASED_POINT_SIZE_RANGE's minimum,
         and a 1px sprite samples the texture at its opaque core, so it would
         clear alphaTest and write depth into the ambient field. The fragment
         discard below is what actually guarantees nothing is drawn while the
         layer is held off. */
      .replace(
        "gl_PointSize = size;",
        `gl_PointSize = size * mix(1.0, mix(1.0, uEmitSize, travel), step(0.001, aEmit))${
          swirl ? " * uSwirlFade" : ""
        };`,
      );

    /* The draw call still has to be issued while the layer is held off - that
       is how the program compiles and the 450,000-point buffers upload behind
       the intro overlay - so the hold-off has to be a no-op in the fragment
       stage rather than a skipped render. Discarding on the same uniform is
       exact: no depth is written and no colour is blended, whatever the driver
       does with point size. Declaration and discard ride the same anchor so
       they cannot half-apply and leave an undeclared uniform behind. */
    if (swirl) {
      shader.fragmentShader = shader.fragmentShader.replace(
        "void main() {",
        `uniform float uSwirlFade;
         void main() {
         if (uSwirlFade <= 0.0) discard;`,
      );
    }

    shaderRef.current = shader;
    // the injection is invisible if it never runs; this makes that observable
    console.info(
      `[particles] drift shader compiled (emit distance ${
        emit.distance ?? 0
      }, speed ${emit.speed ?? 0}, swirl ${swirl ? "on" : "off"})`,
    );
  };
}

/* Random phase offsets, one per particle, consumed by the drift shader. */
export function makePhases(count) {
  const phases = new Float32Array(count);
  for (let i = 0; i < count; i++) phases[i] = Math.random() * Math.PI * 2;
  return phases;
}

/* Per-particle drift multiplier. A single uniform would shake every point
   equally, which softens the very edges that make a shape look clean; this lets
   the surface core sit nearly still while dispersed particles wander freely. */
export function makeUniformDrift(count, value = 1) {
  return new Float32Array(count).fill(value);
}

/* Layers that never emit still need the attributes the shared shader declares. */
export function makeZeroVec3(count) {
  return new Float32Array(count * 3);
}

export function useMousePosition() {
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);

  return mouse;
}
